/**
 * Query keys for project-related queries and mutations
 * Used with React Query to cache and invalidate queries
 */

export const projectKeys = {
  // Base key for all project-related queries
  all: ["projects"] as const,

  // Key for listing projects
  list: (filters?: {
    page?: number;
    take?: number;
    order?: string;
    search?: string;
    shared?: boolean;
    archived?: boolean;
    ownerId?: string;
  }) =>
    filters
      ? ([...projectKeys.all, "list", filters] as const)
      : ([...projectKeys.all, "list"] as const),

  // Key for a specific project details
  detail: (projectId: string | number) => [...projectKeys.all, "detail", projectId] as const,

  /** Project detail with feature_statuses scoped to a document version. */
  detailForDocument: (projectId: string | number, documentId: string | number) =>
    [...projectKeys.detail(projectId), { documentId: String(documentId) }] as const,

  // Key for project counts
  counts: () => [...projectKeys.all, "counts"] as const,

  // Key for the lean per-workspace projects summary (features, members, descriptions)
  summary: (workspaceId: string) => [...projectKeys.all, "summary", workspaceId] as const,

  // Key for the sidebar project switcher's list (license and last change per project)
  switcher: (workspaceId: string) => [...projectKeys.all, "switcher", workspaceId] as const,

  // Key for project team members
  team: (projectId?: string | number) =>
    projectId
      ? ([...projectKeys.all, "team", projectId] as const)
      : ([...projectKeys.all, "team"] as const),

  // Key for the members "Add collaborator" can grant a project to (no id: every project)
  memberCandidates: (projectId?: string) =>
    projectId
      ? ([...projectKeys.all, "member-candidates", projectId] as const)
      : ([...projectKeys.all, "member-candidates"] as const),

  // Key for project roles
  roles: (projectId?: string | number) =>
    projectId
      ? ([...projectKeys.all, "roles", projectId] as const)
      : ([...projectKeys.all, "roles"] as const),

  // Key for project upgrade eligibility
  upgradeEligibility: (projectId: string | number) =>
    [...projectKeys.all, "upgradeEligibility", projectId] as const,
};
